/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/GhesEndpoint/Components/GhesEndpointEditorComponent" />
import * as React from "react";

import { ActionCreatorManager } from "DistributedTask/Flux/ActionCreatorManager";
import { StoreManager } from "DistributedTask/Flux/StoreManager";
import { EndpointsEditorActionsCreator } from "DistributedTaskUI/ServiceEndpoints/Editor/Actions/EndpointsEditorActionsCreator";
import { IEndpointEditorComponentProps } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/EndpointsEditorPanelInterfaces";
import { GenericAuthorizationComponent } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/GenericEndpointComponents/GenericAuthorizationComponent";
import { GenericEndpointsDetailsComponents } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/GenericEndpointComponents/GenericEndpointsDetailsComponents";
import { GenericFooterComponent } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/GenericEndpointComponents/GenericFooterComponent";
import * as Constants from "DistributedTaskUI/ServiceEndpoints/Editor/Constants";
import { ActionsCreatorInstanceIds, TestConnectionDataSource } from "DistributedTaskUI/ServiceEndpoints/Editor/Constants";
import * as Resources from "DistributedTaskUI/ServiceEndpoints/Editor/Resources";
import { EndpointsEditorStore } from "DistributedTaskUI/ServiceEndpoints/Editor/Stores/EndpointsEditorStore";
import { OAuth2Component } from "DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/GhesEndpoint/Components/OAuth2Component";
import { initialServiceConnectionPanelStateHasChanged, IUpdateFormFormInitialFormState } from "DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/GhesEndpoint/GhesEndpointEditorHelper";
import { OAuthConfigOption, OAuthHelper } from "DistributedTaskUI/ServiceEndpoints/Editor/Utils/OAuthHelper";
import { ServiceEndpoint, ServiceEndpointRequest, ServiceEndpointRequestResult } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { ObservableValue } from "VSS/Core/Observable";
import * as Utils_Accessibility from "VSS/Core/Util/Accessibility";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Dropdown } from "VSSUI/Dropdown";
import { Observer } from "VSSUI/Observer";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";

export interface IGhesEndpointEditorComponentProps extends IEndpointEditorComponentProps {
    hidePanelFooter: (hideFooter: boolean) => void;
    closePanel: (serviceEndpoint: ServiceEndpoint) => void;
    onBackButtonClick: () => void;
    hideBackButton: boolean;
    isVerifiableConnection: boolean;
    updateError: (isError: boolean, errorMessage: string, errorStatus?: string) => void;
    errorMessage: string;
}

export interface IGhesEndpointEditorComponentState {
    verifyInProgress: boolean;
    saveInProgress: boolean;
    setupInProgress: boolean;
    isVerifyInProgress?: boolean;
    isVerificationSuccess?: boolean;
    mustAuthorizeConfigId: boolean;
    isError: boolean;
    errorMessage: string;
}

export class GhesEndpointEditorComponent extends VssComponent<IGhesEndpointEditorComponentProps, IGhesEndpointEditorComponentState> {
    private _invalidComponentCount: number;
    private _selection = new DropdownSelection();
    private _isSaveEnabled: ObservableValue<boolean>;
    private _draftServiceEndpoint: ServiceEndpoint;
    private _endpointEditorActionCreator: EndpointsEditorActionsCreator;
    private _endpointEditorStore: EndpointsEditorStore;
    private _updateServiceConnectionFormState: IUpdateFormFormInitialFormState;
    private _oAuthHelper: OAuthHelper;

    constructor(props: IGhesEndpointEditorComponentProps) {
        super(props);
        this._endpointEditorActionCreator = ActionCreatorManager.GetActionCreator<EndpointsEditorActionsCreator>(
            EndpointsEditorActionsCreator,
            ActionsCreatorInstanceIds.EndpointsEditorActionsCreatorInstanceId
        );
        this._endpointEditorStore = StoreManager.GetStore<EndpointsEditorStore>(
            EndpointsEditorStore,
            ActionsCreatorInstanceIds.EndpointsEditorStoreInstanceId
        );
        this._endpointEditorStore.addChangedListener(this._onEndpointEditorStoreChange);
        this._oAuthHelper = new OAuthHelper();

        props.hidePanelFooter(true);

        this._initializeValues();
    }

    public componentDidMount(): void {
        this.setTimeout(() => {
            this.props.updateDraftEndpointState(this._draftServiceEndpoint);
        });
    }

    public componentWillUnmount() {
        this._endpointEditorStore.removeChangedListener(this._onEndpointEditorStoreChange);
    }

    public componentDidUpdate(): void {
       this._draftServiceEndpoint = this.props.draftEndpoint;

       // handle edit panel save state
       if (this.props.isUpdateForm
        && this._draftServiceEndpoint.authorization.scheme === Constants.OAuth2Scheme
        && this._oAuthHelper.getOAuthConfigOption() === OAuthConfigOption.ExistingOAuth) {
            const storeState = this._endpointEditorStore.getState();
            const formContentHasChanged = initialServiceConnectionPanelStateHasChanged(storeState, this._draftServiceEndpoint, this._updateServiceConnectionFormState);
            const oAuthConfigIdHasChanged = this._updateServiceConnectionFormState.savedOAuthConfigId !== this.props.draftEndpoint.authorization.parameters[Constants.ConfigurationId];
            const shouldDisableSaveButtonOnOAuth = this._shouldDisableSaveButtonOnOAuth(oAuthConfigIdHasChanged);
            this._handleUpdateServiceConnectionPanelSaveState(formContentHasChanged, shouldDisableSaveButtonOnOAuth);
            if (oAuthConfigIdHasChanged && !this.state.mustAuthorizeConfigId) {
                this._handleAuthButtonState(true);
            }
       }
    }

    public render(): React.ReactNode {
        return (
            <React.Fragment>
                <div className={"ghes-service-endpoint-editor"}>
                    {this._getAuthenticationMethodPicker()}
                    {this._getGeneralContent()}
                </div>
                { this.state.isError && <MessageCard
                    className="flex-self-stretch margin-top-16 margin-bottom-16"
                    severity={MessageCardSeverity.Error}
                   >
                   {this.state.errorMessage}
                </MessageCard>}
                {this._getFooterComponent()}
            </React.Fragment>
        );
    }

    private _initializeValues() {
        this._invalidComponentCount = this.props.isUpdateForm ? 1 : undefined;
        this._isSaveEnabled = new ObservableValue(false);
        this._isSaveEnabled.value = false;

        this._draftServiceEndpoint = this.props.draftEndpoint;
        this._draftServiceEndpoint.data = this._draftServiceEndpoint.data || {};

        let selectedSchemeIndex = 0;
        if (this.props.isUpdateForm) {
            selectedSchemeIndex = this._getSelectedSchemeIndex();
        }
        this._selection.select(selectedSchemeIndex);

        if (this.props.isUpdateForm) {
            const storeState = this._endpointEditorStore.getState();
            const isOAuth = this._draftServiceEndpoint.authorization.scheme === Constants.OAuth2Scheme;
            const isUsernamePassword = this._draftServiceEndpoint.authorization.scheme === Constants.UsernamePassword;
            this._updateServiceConnectionFormState = {
                authenticationMethodIndex: selectedSchemeIndex,
                acceptUntrustedCerts: (this.props.draftEndpoint.data && this.props.draftEndpoint.data.acceptUntrustedCerts === "true") ? true : false,
                allowAllPipelinesFlagState: null,
                serviceConnectionName: storeState.serviceEndpoint.name,
                savedOAuthConfigId: isOAuth ? this.props.draftEndpoint.authorization.parameters[Constants.ConfigurationId] : null,
                endpointUrl: !isOAuth ? this.props.draftEndpoint.url : null,
                username: isUsernamePassword ? this.props.draftEndpoint.authorization.parameters[Constants.Username] : null
            };
        }

        this.state = {
            verifyInProgress: false,
            saveInProgress: false,
            setupInProgress: false,
            mustAuthorizeConfigId: this.props.isUpdateForm ? false : true,
            isError: false,
            errorMessage: ""
        };
    }

    private _getAuthenticationMethodPicker(): JSX.Element {
        return this.props.serviceEndpointType && this.props.serviceEndpointType.authenticationSchemes.length > 1 ? (
            <div className="auth-scheme-selection-section">
                <label className="bolt-formitem-label body-m">{Resources.AuthenticationPickerHeader}</label>
                <Dropdown
                    className="auth-scheme-picker"
                    selection={this._selection}
                    items={this.props.serviceEndpointType.authenticationSchemes.map(authScheme => {
                        const displayName =
                            authScheme.scheme === Constants.OAuth2Scheme ? authScheme.displayName + Resources.Recommended : authScheme.displayName;
                        return {
                            id: authScheme.scheme,
                            text: displayName
                        };
                    })}
                    onSelect={(ev, selectedScheme) => {
                        this._draftServiceEndpoint.authorization.scheme = selectedScheme.id;
                        this._draftServiceEndpoint.authorization.parameters = {};
                        this.props.updateDraftEndpointState(this._draftServiceEndpoint);
                        this._invalidComponentCount = this.props.isUpdateForm ? 1 : undefined;
                        this._updateVerificationStatus(undefined, undefined);
                        this.setState({mustAuthorizeConfigId: true});
                    }}
                />
            </div>
        ) : (
            <div />
        );
    }

    private _getGeneralContent(): JSX.Element {
        if (this._draftServiceEndpoint.authorization.scheme === Constants.OAuth2Scheme) {
            const storeState = this._endpointEditorStore.getState();
            const hideAuthButton = this.props.isUpdateForm && this._oAuthHelper.getOAuthConfigOption() === OAuthConfigOption.ExistingOAuth && !this.state.mustAuthorizeConfigId;
            const hideButtonClassName = hideAuthButton ? " hide-oauth-btn" : "";
            return (
                <div className={hideButtonClassName}>
                     <OAuth2Component
                        {...this.props}
                        invalidComponentCount={this._invalidComponentCount}
                        updateInvalidComponentCount={(count: number) => {
                            this._updateInvalidComponentCount(count);
                        }}
                        updateInvalidInputsCount={(count: number) => {
                            this._updateInvalidComponentCount(count);
                        }}
                        hasConfigurations={storeState.configurations && storeState.configurations.length > 0}
                        serviceEndpointComponent={this._getServiceEndpointComponent(true)}
                        oAuthHelper={this._oAuthHelper}
                    />
                </div>
            );
        }
        // Use the general content for PAT and Username/Password flow
        return (
            <div>
                <GenericAuthorizationComponent
                    serviceEndpointType={this.props.serviceEndpointType}
                    draftEndpoint={this.props.draftEndpoint}
                    isUpdateForm={this.props.isUpdateForm}
                    updateDraftEndpointState={this.props.updateDraftEndpointState}
                    updateInvalidInputsCount={(count: number) => {
                        this._updateInvalidComponentCount(count);
                    }}
                    disableAllPipelinesSetting={this.props.disableAllPipelinesSetting}
                />
                {/**This adds service connection section */}
                {this._getServiceEndpointComponent(false)}
            </div>
        );
    }

    private _updateInvalidComponentCount(count: number) {
        this._invalidComponentCount = this._invalidComponentCount ? this._invalidComponentCount + count : count;
        const isVerificationSuccess = this._draftServiceEndpoint.authorization.scheme !== Constants.OAuth2Scheme ? (this.state.isVerificationSuccess === undefined ? true : this.state.isVerificationSuccess) : true;
        this._isSaveEnabled.value = this._invalidComponentCount < 1 && isVerificationSuccess;
    }

    private _getServiceEndpointComponent(isOAuthComponent: boolean): JSX.Element {
        if (!isOAuthComponent && this.props.isUpdateForm && this._updateServiceConnectionFormState.serviceConnectionName && !this.props.draftEndpoint.name) {
            this.props.draftEndpoint.name = this._updateServiceConnectionFormState.serviceConnectionName;
        }
        return (
            <GenericEndpointsDetailsComponents
                serviceEndpoint={this.props.draftEndpoint}
                isUpdateForm={this.props.isUpdateForm}
                updateDraftEndpointState={this.props.updateDraftEndpointState}
                updateInvalidInputsCount={(count: number) => {
                    if (isOAuthComponent) {
                        if (count > 0 && !this._oAuthHelper.getServiceEndpointComponentIsInvalid()) {
                            this._oAuthHelper.updateServiceEndpointComponentIsInvalid(true);
                            this._updateInvalidComponentCount(count);
                        } else if (count < 0 && this._oAuthHelper.getServiceEndpointComponentIsInvalid()) {
                            this._oAuthHelper.updateServiceEndpointComponentIsInvalid(false);
                            this._updateInvalidComponentCount(count);
                        }
                    }
                    else {
                        this._updateInvalidComponentCount(count);
                    }
                }}
                updateAllowAllPipelinesFlag={(checked) => {
                    // Because allowAllPipelinesFlagState updates after initial render, set initial state on first update.
                    if (this.props.isUpdateForm && this._updateServiceConnectionFormState.allowAllPipelinesFlagState === null) {
                        this._updateServiceConnectionFormState.allowAllPipelinesFlagState = !checked;
                    }
                    this.props.updateAllowAllPipelinesFlag(checked);
                }}
                allowAllPipelinesFlagState={this.props.allowAllPipelinesFlagState}
                disableAllPipelinesSetting={this.props.disableAllPipelinesSetting}
            />
        );
    }

    private _getFooterComponent(): React.ReactNode {
        return (
            <Observer enableSave={this._isSaveEnabled}>
                {(props: { enableSave: boolean }) => {
                    return (
                        <GenericFooterComponent
                            isUpdateForm={this.props.isUpdateForm}
                            enableSave={props.enableSave}
                            serviceEndpoint={this._draftServiceEndpoint}
                            isVerifiable={this.props.isVerifiableConnection}
                            showSaveButton={true}
                            showBackButton={!this.props.hideBackButton}
                            verifyInProgress={this.state.verifyInProgress}
                            saveInProgress={this.state.saveInProgress}
                            setupInProgress={this.state.setupInProgress}
                            onVerifyAndSaveButtonClick={this._verifyAndSaveServiceEndpoint}
                            onBackButtonClick={() => {
                                this.props.onBackButtonClick();
                                this._draftServiceEndpoint.authorization.parameters = {};
                                this.props.updateDraftEndpointState(this._draftServiceEndpoint);
                            }}
                            onSaveButtonClick={this._saveServiceEndpoint}
                        />
                    );
                }}
            </Observer>
        );
    }

    private _getSelectedSchemeIndex() {
        const selectedScheme = this.props.draftEndpoint.authorization && this.props.draftEndpoint.authorization.scheme;
        return this.props.serviceEndpointType.authenticationSchemes.findIndex((scheme => scheme.scheme === selectedScheme));
    }

    private _isValidUpdatePanelChangedAuthMethodState() {
        const draftEndpointAuthorization = this.props.draftEndpoint.authorization;
        if (draftEndpointAuthorization.scheme === Constants.TokenScheme) {
            return !!draftEndpointAuthorization.parameters.apitoken && !!this.props.draftEndpoint.url;
        } else if (draftEndpointAuthorization.scheme === Constants.UsernamePassword) {
            return !!draftEndpointAuthorization.parameters[Constants.Username] && !!draftEndpointAuthorization.parameters[Constants.Password] && !!this.props.draftEndpoint.url;
        } else if (draftEndpointAuthorization.scheme === Constants.OAuth2Scheme) {
            return !!draftEndpointAuthorization.parameters[Constants.ConfigurationId] && !!this.props.draftEndpoint.authorization.parameters[Constants.AccessToken];
        }
    }

    private _shouldDisableSaveButtonOnOAuth(configIdHasChanged: boolean) {
        if (this.props.draftEndpoint.authorization.scheme !== Constants.OAuth2Scheme) {
            return false;
        }

        if (!this.state.mustAuthorizeConfigId) {
            if (configIdHasChanged) {
                return true;
            }
            return false;
        }
        return !this.props.draftEndpoint.authorization.parameters[Constants.AccessToken] || !this.props.draftEndpoint.authorization.parameters[Constants.ConfigurationId];
    }

    private _handleUpdateServiceConnectionPanelSaveState(formContentHasChanged: boolean, shouldDisableSaveButtonOnOAuth: boolean) {
        if (formContentHasChanged && !shouldDisableSaveButtonOnOAuth) {
            // if any of the saved inputs are changed, update invalidComponent count to 0
            if (this._invalidComponentCount !== 0) {
                this._invalidComponentCount = 0;
            }
        } else if (this._invalidComponentCount < 1) {
            // if the state is the same as the original update invalidComponent count to 1
            this._invalidComponentCount = 1;
        }
        const isVerificationSuccess = this._draftServiceEndpoint.authorization.scheme !== Constants.OAuth2Scheme ? (this.state.isVerificationSuccess === undefined ? true : this.state.isVerificationSuccess) : true;
        this._isSaveEnabled.value = this._invalidComponentCount < 1 && isVerificationSuccess;
    }

    private _handleAuthButtonState(state: boolean) {
        this.setState({ mustAuthorizeConfigId: state });
    } 

    private _onEndpointEditorStoreChange = () => {
        const storeState = this._endpointEditorStore.getState();
        if (this.props.isUpdateForm) {
            // Handle the save button state when using the _edit_ service connection panel.
            const isChangedAuthMethod = this._updateServiceConnectionFormState.authenticationMethodIndex !== this._getSelectedSchemeIndex();
            if (isChangedAuthMethod) {
                const isValidPanel = this._isValidUpdatePanelChangedAuthMethodState();
                this._invalidComponentCount = isValidPanel ? 0 : 1;
            } else {
                // Auth method has not changed - check if inputs have changed
                const formContentHasChanged = initialServiceConnectionPanelStateHasChanged(storeState, this._draftServiceEndpoint, this._updateServiceConnectionFormState);
                const oAuthConfigIdHasChanged = this.props.draftEndpoint.authorization.scheme === Constants.OAuth2Scheme ? this._updateServiceConnectionFormState.savedOAuthConfigId !== this.props.draftEndpoint.authorization.parameters[Constants.ConfigurationId] : false;
                const shouldDisableSaveButtonOnOAuth = this._shouldDisableSaveButtonOnOAuth(oAuthConfigIdHasChanged);
                // Handle oauth auth button visibility
                if (formContentHasChanged && shouldDisableSaveButtonOnOAuth && oAuthConfigIdHasChanged && !this.state.mustAuthorizeConfigId) {
                    this._handleAuthButtonState(true);
                }
                this._handleUpdateServiceConnectionPanelSaveState(formContentHasChanged, shouldDisableSaveButtonOnOAuth);
            }
        }

        if (this.state.saveInProgress && !storeState.setupInProgress && !storeState.saveInProgress && !storeState.errorMessage) {
            setTimeout(() => {
                this.props.closePanel(storeState.serviceEndpoint);
            });
        }

        if (this.state.setupInProgress && !storeState.setupInProgress && !storeState.errorMessage) {
            setTimeout(() => {
                this.props.closePanel(storeState.serviceEndpoint);
            });
        }

        this.setState({
            verifyInProgress: storeState.verifyInProgress,
            saveInProgress: storeState.saveInProgress,
            setupInProgress: storeState.setupInProgress,
            isError: storeState.isError,
            errorMessage: storeState.errorMessage
        });
    }

    private _verifyAndSaveServiceEndpoint = () => {
        this._endpointEditorActionCreator.verifyAndSaveServiceEndpoint(
            this._draftServiceEndpoint,
            TestConnectionDataSource,
            this.props.allowAllPipelinesFlagState,
            this.props.isUpdateForm
        );
    }

    private _saveServiceEndpoint = () => {
        if (this.props.isUpdateForm) {
            this._endpointEditorActionCreator
                .updateServiceEndpoint(this._draftServiceEndpoint)
                .then((endpoint: ServiceEndpoint) => {
                    this._endpointEditorActionCreator.saveResourceAuthorizationState(
                        this._draftServiceEndpoint.id,
                        this.props.allowAllPipelinesFlagState
                    );
                    this.props.closePanel(endpoint);
                })
                .catch(error => {
                    const errorMessage: string = error.message ? error.message : Resources.ValidateEndpointErrorMessage;
                    this._updateError(true, errorMessage);
                });
        } else {
            this._endpointEditorActionCreator
                .saveServiceEndpoint(this.props.draftEndpoint)
                .then((endpoint: ServiceEndpoint) => {
                    this._endpointEditorActionCreator.saveResourceAuthorizationState(endpoint.id, this.props.allowAllPipelinesFlagState);
                    this.props.closePanel(endpoint);
                })
                .catch(error => {
                    const errorMessage: string = error.message ? error.message : Resources.ValidateEndpointErrorMessage;
                    this._updateError(true, errorMessage);
                });
        }
    }

    private _updateError = (isError: boolean, errorMessage: string, errorStatus?: string): void => {
        if (isError && !errorStatus) {
            Utils_Accessibility.announce(errorMessage);
        }
        this.setState({
            isError: isError,
            errorMessage: errorMessage
        });
    }

    private _updateVerificationStatus = (isVerifyInProgress: boolean, isVerificationSuccess: boolean): void => {
        this.setState({
            isVerifyInProgress: isVerifyInProgress,
            isVerificationSuccess: isVerificationSuccess
        });
    }
}

VssComponent.register("endpoint-editor-component-ghes", GhesEndpointEditorComponent);
